import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const springEasing = [0.22, 1, 0.36, 1];

const mobileMenuVariants = {
  closed: { x: '100%', transition: { duration: 0.35, ease: springEasing } },
  open: { x: 0, transition: { duration: 0.4, ease: springEasing } },
};

const ServicesNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [['#features', 'Tjenester', '/landing-page'], ['/services-detail', 'Detaljer', null], ['/contact', 'Kontakt', null], ['/trust-and-security', 'Sikkerhet', null]];

  const handleNavClick = (href, fallback) => {
    setMobileOpen(false);
    if (href?.startsWith('#')) {
      navigate(fallback || '/landing-page');
    } else {
      navigate(href);
    }
  };

  return (
    <>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        height: '64px', background: 'rgba(10,12,16,0.92)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(224,228,232,0.06)', display: 'flex', alignItems: 'center', padding: '0 32px',
      }}>
        <div style={{ maxWidth: '1440px', width: '100%', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={() => navigate('/landing-page')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', padding: 0 }}>
            <div style={{ width: '28px', height: '28px', background: '#FF5722', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5"><polygon points="3 11 22 2 13 21 11 13 3 11" /></svg>
            </div>
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: '16px', fontWeight: 700, color: 'var(--color-foreground)' }}>Veiassistent</span>
          </button>

          <div className="hidden-mobile" style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            {navLinks?.map(([href, label, fallback]) => {
              const isActive = !href?.startsWith('#') && location?.pathname === (fallback || href);
              return (
                <button key={href} onClick={() => handleNavClick(href, fallback)}
                  style={{
                    background: 'none', border: 'none',
                    borderBottom: isActive ? '2px solid #FF5722' : '2px solid transparent',
                    padding: '2px 0', fontFamily: 'var(--font-heading)', fontSize: '13px',
                    fontWeight: isActive ? 700 : 600, letterSpacing: '0.06em', textTransform: 'uppercase',
                    color: isActive ? '#FF5722' : 'var(--color-muted-foreground)', cursor: 'pointer',
                    transition: 'color 150ms ease, border-color 150ms ease',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = 'var(--color-foreground)'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = 'var(--color-muted-foreground)'; }}
                >{label}</button>
              );
            })}
          </div>

          <div className="hidden-mobile" style={{ display: 'flex', gap: '12px' }}>
            <button onClick={() => navigate('/login')} style={{ background: 'transparent', border: '1px solid rgba(224,228,232,0.2)', borderRadius: '2px', padding: '8px 20px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-foreground)', cursor: 'pointer' }}>Logg inn</button>
            <button onClick={() => navigate('/register')} style={{ background: '#FF5722', border: '2px solid #FF5722', borderRadius: '2px', padding: '8px 20px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#FFFFFF', cursor: 'pointer' }}>Registrer deg</button>
          </div>

          <button className="show-mobile" onClick={() => setMobileOpen(p => !p)}
            style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: '8px', zIndex: 110 }}>
            <div style={{ width: '24px', height: '20px', position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <motion.span animate={mobileOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }} transition={{ duration: 0.3 }} style={{ display: 'block', height: '2px', width: '100%', background: mobileOpen ? '#FF5722' : 'var(--color-foreground)', borderRadius: '2px', transformOrigin: 'center' }} />
              <motion.span animate={mobileOpen ? { opacity: 0 } : { opacity: 1 }} transition={{ duration: 0.2 }} style={{ display: 'block', height: '2px', width: '100%', background: 'var(--color-foreground)', borderRadius: '2px' }} />
              <motion.span animate={mobileOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }} transition={{ duration: 0.3 }} style={{ display: 'block', height: '2px', width: '100%', background: mobileOpen ? '#FF5722' : 'var(--color-foreground)', borderRadius: '2px', transformOrigin: 'center' }} />
            </div>
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div variants={{ closed: { opacity: 0 }, open: { opacity: 1 } }} initial="closed" animate="open" exit="closed"
              onClick={() => setMobileOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 108, backdropFilter: 'blur(4px)' }} />
            <motion.div variants={mobileMenuVariants} initial="closed" animate="open" exit="closed"
              style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '280px', background: 'rgba(18,20,24,0.98)', borderLeft: '1px solid rgba(224,228,232,0.1)', zIndex: 109, padding: '80px 24px 32px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {navLinks?.map(([href, label, fallback], i) => {
                const isActive = !href?.startsWith('#') && location?.pathname === (fallback || href);
                return (
                  <motion.button key={href} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.07 }}
                    onClick={() => handleNavClick(href, fallback)}
                    style={{ background: 'none', border: 'none', borderLeft: isActive ? '3px solid #FF5722' : '3px solid transparent', padding: '12px 16px', fontFamily: 'var(--font-heading)', fontSize: '14px', fontWeight: isActive ? 700 : 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: isActive ? '#FF5722' : 'var(--color-foreground)', cursor: 'pointer', textAlign: 'left' }}>
                    {label}
                  </motion.button>
                );
              })}
              <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button onClick={() => { setMobileOpen(false); navigate('/login'); }} style={{ background: 'transparent', border: '1px solid rgba(224,228,232,0.2)', borderRadius: '2px', padding: '12px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-foreground)', cursor: 'pointer' }}>Logg inn</button>
                <button onClick={() => { setMobileOpen(false); navigate('/register'); }} style={{ background: '#FF5722', border: '2px solid #FF5722', borderRadius: '2px', padding: '12px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#FFFFFF', cursor: 'pointer' }}>Registrer deg</button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default ServicesNav;
